(() => {
  const P = () => window.GhaliaPricing;
  const moneyNet = (cents) => P()?.formatDisplay(cents) ?? (Number(cents) / 100).toFixed(2);
  const moneyDisplay = (cents) => P()?.formatDisplay(cents) ?? (Number(cents) / 100).toFixed(2);

  const readThemeConfig = (drawer) => ({
    enabled: drawer?.dataset.globalDiscountEnabled === 'true',
    pct: Number(drawer?.dataset.globalDiscountPct || 0),
    maxNet: Number(drawer?.dataset.globalDiscountMaxNet || 0),
    label: drawer?.dataset.globalDiscountLabel || 'Extra savings',
  });

  const vatRateFromDrawer = (drawer) =>
    Number(drawer?.querySelector('[data-cart-summary]')?.dataset.vatRate || 15);

  /** Theme SAR settings are VAT-inclusive customer amounts → ex-VAT cents for cart math. */
  const sarGrossToNetCents = (sar, drawer) => {
    if (!sar) return 0;
    const vatRate = vatRateFromDrawer(drawer);
    return Math.round((sar * 100 * 100) / (100 + vatRate));
  };

  const promoCfg = () => window.GhaliaPromoConfig;
  const qualifyingMeetsPromoMinimum = (qualifyingNet, drawer) =>
    promoCfg()?.meetsMinimum?.(qualifyingNet, drawer) ?? true;
  const capPromoSavings = (savings, drawer) =>
    promoCfg()?.capNetSavings?.(savings, drawer) ?? savings;
  const formatPromoAmount = (netCents, pricesIncludeVat) =>
    promoCfg()?.formatAmount?.(netCents, pricesIncludeVat) ?? moneyNet(netCents);

  const PROMO_STORAGE_KEY = 'ghalia_cart_promo_code';

  const storedPromoCode = () => {
    try {
      return sessionStorage.getItem(PROMO_STORAGE_KEY) || '';
    } catch (_) {
      return '';
    }
  };

  const promoTitleFromLines = (cart) => {
    for (const item of cart?.items || []) {
      for (const alloc of item.line_level_discount_allocations || []) {
        const title = alloc?.discount_application?.title;
        if (title) return String(title).trim();
      }
    }
    return '';
  };

  const inferredPromoSavingsNet = (cart, drawer) => {
    const pricing = P();
    if (!pricing || !drawer) return 0;
    let sum = 0;
    (cart?.items || []).forEach((item) => {
      if (pricing.isNativeBundleLine(item) || pricing.isFreeGiftLine(item)) return;
      const row = drawer.querySelector(`[data-cart-line-key="${item.key}"]`);
      const domNet = Number(row?.dataset?.cartLineNet || 0);
      const originalNet =
        domNet > 0 ? domNet : Number(item.original_line_price) || Number(item.line_price) || 0;
      const finalNet = Number(item.final_line_price) || 0;
      if (originalNet > finalNet) sum += originalNet - finalNet;
    });
    return sum;
  };

  const promoCodeApplication = (cart, drawer) => {
    const apps = cart?.cart_level_discount_applications || [];
    const fromCart = apps.find((d) => d?.type === 'discount_code') || apps[0];
    if (fromCart?.title) return fromCart;

    const lineTitle = promoTitleFromLines(cart);
    const fromLines = linePromoAllocationNet(cart);
    const inferred = drawer ? inferredPromoSavingsNet(cart, drawer) : 0;
    const savings = fromLines > 0 ? fromLines : inferred;
    const stored = storedPromoCode();
    const title = lineTitle || stored;

    if (!title) return null;

    const totalDiscount = Number(cart?.total_discount) || 0;
    const allocated = savings > 0 ? savings : totalDiscount;
    return {
      type: 'discount_code',
      title,
      total_allocated_amount: allocated,
      value_type: 'percentage',
      value: 10,
    };
  };

  const promoPercent = (promo) => {
    const raw = Number(promo?.value) || 0;
    if (promo?.value_type !== 'percentage') return 0;
    return raw <= 1 ? raw * 100 : raw;
  };

  const linePromoAllocationNet = (cart) => {
    const pricing = P();
    return (cart?.items || []).reduce((sum, item) => {
      if (pricing?.isNativeBundleLine?.(item) || pricing?.isFreeGiftLine?.(item)) return sum;
      return (
        sum +
        (item.line_level_discount_allocations || []).reduce(
          (lineSum, alloc) => lineSum + (Number(alloc.amount) || 0),
          0
        )
      );
    }, 0);
  };

  /** Catalog ex-VAT subtotal (pre promo) — mirrors cart-line-net-cents / data-cart-line-net. */
  const catalogNetSubtotal = (cart, drawer, pricing) => {
    let sum = 0;
    (cart?.items || []).forEach((item) => {
      if (pricing.isFreeGiftLine(item)) return;
      const row = drawer?.querySelector(`[data-cart-line-key="${item.key}"]`);
      const catalog =
        Number(row?.dataset?.cartLineNet || 0) ||
        Number(item.original_line_price) ||
        Number(item.original_price ?? item.price ?? 0) * (Number(item.quantity) || 1);
      sum += catalog;
    });
    return sum;
  };

  /** Shopify code promos update line final_line_price — do not subtract promo row twice. */
  const promoAlreadyInSubtotal = (cart, drawer, pricing, promoSavings, netSubtotal) => {
    if (promoSavings <= 0) return false;
    if (linePromoAllocationNet(cart) > 0) return true;
    const catalogNet = catalogNetSubtotal(cart, drawer, pricing);
    if (catalogNet > netSubtotal && catalogNet - netSubtotal >= promoSavings - 1) return true;
    const inferred = inferredPromoSavingsNet(cart, drawer);
    return inferred > 0 && Math.abs(inferred - promoSavings) <= 1;
  };

  const promoLabelForCart = (cart, drawer) => {
    const lineTitle = promoTitleFromLines(cart);
    const stored = storedPromoCode();
    const defaultCode = promoCfg()?.read?.(drawer)?.defaultCode || 'WELCOME';
    return lineTitle || stored || defaultCode;
  };

  const resolvePromoState = (cart, drawer, stackState, cfg) => {
    const pricing = P();
    if (!pricing || !drawer) return { savings: 0, label: '', active: false };

    const qualifyingNet = pricing.qualifyingSubtotalNet(cart, drawer);
    if (qualifyingNet <= 0 || !qualifyingMeetsPromoMinimum(qualifyingNet, drawer)) {
      return { savings: 0, label: '', active: false };
    }

    const label = promoLabelForCart(cart, drawer);
    const shopifyNet = linePromoAllocationNet(cart);
    const hasPromoSignal =
      shopifyNet > 0 ||
      Number(cart?.total_discount) > 0 ||
      Boolean(storedPromoCode()) ||
      (cfg.enabled && cfg.pct > 0);

    if (!hasPromoSignal) return { savings: 0, label: '', active: false };

    const savings = promoCfg()?.resolveSavingsNet?.(cart, drawer, qualifyingNet) || 0;
    return { savings, label, active: savings > 0 };
  };

  const lineUnitNet = (item, row, pricing) => {
    const qty = Number(item.quantity) || 1;
    const { net, unitNet } = pricing.cartLineAmounts(item, row);
    if (unitNet > 0) return unitNet;
    return Math.round(net / qty);
  };

  const lineOriginalNet = (item, row) => {
    const qty = Number(item.quantity) || 1;
    const domNet = Number(row?.dataset?.cartLineNet || 0);
    if (domNet > 0) return domNet;
    const origLine = Number(item.original_line_price) || 0;
    if (origLine > 0) return origLine;
    const unit = Number(item.original_price ?? item.price ?? 0);
    if (unit > 0) return unit * qty;
    return Number(item.line_price) || 0;
  };

  const setCustomerLinePrice = (currentEl, pricing, netCents, row) => {
    if (!currentEl || !pricing) return;
    const next = pricing.formatCustomerLineNet
      ? pricing.formatCustomerLineNet(netCents, row)
      : pricing.formatNet(netCents);
    if (currentEl.textContent.trim() !== next.trim()) currentEl.textContent = next;
  };

  const showPromoLinePrice = (priceEl, compareEl, currentEl, pricing, originalNet, finalNet, row) => {
    if (!currentEl || originalNet <= 0) return;
    setCustomerLinePrice(currentEl, pricing, finalNet, row);
    if (compareEl && originalNet > finalNet) {
      setCustomerLinePrice(compareEl, pricing, originalNet, row);
      compareEl.hidden = false;
      compareEl.removeAttribute('hidden');
    } else if (compareEl) {
      compareEl.hidden = true;
    }
    priceEl?.classList.add('is-promo-discounted');
  };

  const bundleAndPromoUnits = (item, stackState) => {
    const pricing = P();
    const qty = Number(item.quantity) || 1;
    if (pricing?.isNativeBundleLine?.(item)) {
      return { bundleUnits: qty, promoUnits: 0, qty };
    }
    const fromLine = stackState?.bundleUnitsByLine?.get(item.key);
    const bundleUnits =
      stackState?.memberSet?.has(item.handle) && fromLine > 0
        ? fromLine
        : 0;
    const promoUnits = Math.max(0, qty - bundleUnits);
    return { bundleUnits, promoUnits, qty };
  };

  const applyPromoLinePrices = (cart, drawer, stackState, promoSavings, qualifyingNet) => {
    const pricing = P();
    if (!pricing || !drawer) return;
    if (!qualifyingMeetsPromoMinimum(qualifyingNet, drawer)) {
      (cart?.items || []).forEach((item) => {
        if (pricing.isFreeGiftLine(item) || pricing.isNativeBundleLine(item)) return;
        const row = drawer.querySelector(`[data-cart-line-key="${item.key}"]`);
        if (!row) return;
        const priceEl = row.querySelector('[data-cart-line-price]');
        const compareEl = row.querySelector('[data-cart-line-compare]');
        const currentEl = row.querySelector('[data-cart-line-current]');
        if (!currentEl) return;
        const originalNet = lineOriginalNet(item, row);
        if (originalNet > 0) setCustomerLinePrice(currentEl, pricing, originalNet, row);
        if (compareEl) compareEl.hidden = true;
        priceEl?.classList.remove('is-promo-discounted');
      });
      return;
    }
    if (promoSavings <= 0) return;

    (cart?.items || []).forEach((item) => {
      if (pricing.isFreeGiftLine(item) || pricing.isNativeBundleLine(item)) return;

      const row = drawer.querySelector(`[data-cart-line-key="${item.key}"]`);
      if (!row) return;
      const priceEl = row.querySelector('[data-cart-line-price]');
      const compareEl = row.querySelector('[data-cart-line-compare]');
      const currentEl = row.querySelector('[data-cart-line-current]');
      if (!priceEl || !currentEl) return;

      const allocNet = (item.line_level_discount_allocations || []).reduce(
        (sum, alloc) => sum + (Number(alloc.amount) || 0),
        0
      );
      const originalNet = lineOriginalNet(item, row);

      if (allocNet > 0) {
        const finalNet = Number(item.final_line_price) || Math.max(0, originalNet - allocNet);
        showPromoLinePrice(priceEl, compareEl, currentEl, pricing, originalNet, finalNet, row);
        return;
      }

      const { bundleUnits, promoUnits } = bundleAndPromoUnits(item, stackState);
      if (promoUnits <= 0 && bundleUnits <= 0) return;

      const qtySafe = Number(item.quantity) || 1;
      const unitOriginalNet = Math.round(originalNet / qtySafe);

      let promoLineNet = unitOriginalNet * promoUnits;
      let finalPromoNet = promoLineNet;
      if (promoUnits > 0 && promoSavings > 0 && qualifyingNet > 0) {
        const lineShare = Math.round((promoSavings * promoLineNet) / qualifyingNet);
        finalPromoNet = Math.max(0, promoLineNet - lineShare);
      }

      let bundlePartNet = 0;
      if (bundleUnits > 0 && stackState?.qualified && stackState?.pct > 0) {
        const unitNet = lineUnitNet(item, row, pricing);
        const discountedUnit = pricing.applyDiscount(unitNet, stackState.pct);
        bundlePartNet = discountedUnit * bundleUnits;
      } else if (bundleUnits > 0) {
        bundlePartNet = unitOriginalNet * bundleUnits;
      }

      const currentNet = bundlePartNet + finalPromoNet;
      const compareNet = originalNet;

      if (promoUnits > 0 && finalPromoNet < promoLineNet) {
        showPromoLinePrice(priceEl, compareEl, currentEl, pricing, compareNet, currentNet, row);
      } else if (currentNet < compareNet) {
        showPromoLinePrice(priceEl, compareEl, currentEl, pricing, compareNet, currentNet, row);
      }
    });
  };

  const hideLiquidDiscountRows = (drawer) => {
    drawer?.querySelectorAll('[data-cart-liquid-discount]').forEach((el) => {
      el.hidden = true;
    });
  };

  const estimateShippingNet = (itemsNet, summary) => {
    if (!summary) return 0;
    const threshold = Number(summary.dataset.shippingThresholdCents) || 60000;
    const flat = Number(summary.dataset.shippingFlatCents) || 2500;
    return itemsNet >= threshold ? 0 : flat;
  };

  const shippingNetCents = (drawer, itemsNetAfterDiscounts = 0) => {
    const summary = drawer?.querySelector('[data-cart-summary]');
    if (itemsNetAfterDiscounts > 0 && summary) {
      return estimateShippingNet(itemsNetAfterDiscounts, summary);
    }
    if (summary?.dataset.shippingNetCents != null && summary.dataset.shippingNetCents !== '') {
      return Number(summary.dataset.shippingNetCents) || 0;
    }
    return 0;
  };

  /** Sum of Shopify final_line_price (ex-VAT) — matches checkout subtotal after line discounts. */
  const finalItemsNet = (cart, pricing) => {
    let sum = 0;
    (cart?.items || []).forEach((item) => {
      if (pricing.isFreeGiftLine(item)) return;
      sum += Number(item.final_line_price) || Number(item.line_price) || 0;
    });
    return sum;
  };

  const applySummary = (cart, stackState, drawer) => {
    const pricing = P();
    if (!pricing || !drawer) return;

    hideLiquidDiscountRows(drawer);

    const cfg = readThemeConfig(drawer);
    const globalRow = drawer.querySelector('[data-cart-global-discount-row]');
    const summary = drawer.querySelector('[data-cart-summary]');
    const promoRow = drawer.querySelector('[data-cart-promo-code-row]');
    const promoLabel = drawer.querySelector('[data-cart-promo-code-label]');
    const promoAmount = drawer.querySelector('[data-cart-promo-code-amount]');
    const subtotalEl = drawer.querySelector('[data-cart-summary-subtotal]');
    const shippingEl = drawer.querySelector('[data-cart-summary-shipping]');
    const vatEl = drawer.querySelector('[data-cart-summary-vat]');
    const vatRow = vatEl?.closest('.cart-summary__row--vat');
    const totalEl = drawer.querySelector('[data-cart-summary-total]');
    const pricesIncludeVat = pricing.catalogPricesExcludeVat?.() ?? true;

    if (vatRow) {
      vatRow.hidden = pricesIncludeVat;
      if (pricesIncludeVat) vatRow.setAttribute('hidden', '');
      else vatRow.removeAttribute('hidden');
    }

    const netSubtotal = stackState?.netSubtotalCents || 0;
    const catalogNet = catalogNetSubtotal(cart, drawer, pricing);
    const bundleSavings = stackState?.bundleSavingsNetCents || 0;
    const qualifyingNet = pricing.qualifyingSubtotalNet(cart, drawer);
    const promo = resolvePromoState(cart, drawer, stackState, cfg);
    const promoDisplaySavings = promo.active ? promo.savings : 0;
    const promoLabelText = promo.label || '';

    if (globalRow) globalRow.hidden = true;

    if (promoRow && promoLabel && promoAmount) {
      if (promoDisplaySavings > 0 && promoLabelText) {
        promoRow.hidden = false;
        promoRow.removeAttribute('hidden');
        const codeLabel = String(promoLabelText).toUpperCase();
        promoLabel.textContent =
          promoLabel.dataset.codeTemplate?.replace('__TITLE__', codeLabel) || codeLabel;
        promoAmount.textContent = `-${formatPromoAmount(promoDisplaySavings, pricesIncludeVat)}`;
      } else {
        promoRow.hidden = true;
        promoRow.setAttribute('hidden', '');
        promoLabel.textContent = '';
        promoAmount.textContent = '';
      }
    }

    applyPromoLinePrices(cart, drawer, stackState, promoDisplaySavings, qualifyingNet);

    const itemsCatalogNet = catalogNet > 0 ? catalogNet : netSubtotal;
    if (itemsCatalogNet <= 0) return;

    const netBeforePromo = Math.max(0, itemsCatalogNet - bundleSavings);
    const shopifyItemsNet = finalItemsNet(cart, pricing);
    const netAfterDiscounts =
      shopifyItemsNet > 0 && promoDisplaySavings > 0
        ? shopifyItemsNet
        : Math.max(0, netBeforePromo - promoDisplaySavings);
    const shippingNet = shippingNetCents(drawer, netAfterDiscounts);
    const taxableNet = netAfterDiscounts + shippingNet;
    const vatCents = pricing.vatOnNet(taxableNet);
    const totalNet = taxableNet + vatCents;

    const formatNetAsGross = (netCents) =>
      pricesIncludeVat ? pricing.formatNet(netCents) : moneyNet(netCents);
    const formatGross = (grossCents) =>
      pricesIncludeVat ? pricing.formatDisplay(grossCents) : moneyNet(grossCents);

    if (subtotalEl) subtotalEl.textContent = formatNetAsGross(netBeforePromo);
    if (summary) summary.dataset.shippingNetCents = String(shippingNet);
    if (shippingEl) {
      shippingEl.textContent =
        shippingNet === 0
          ? shippingEl.dataset.freeLabel || 'Free'
          : formatNetAsGross(shippingNet);
    }
    if (vatEl && !pricesIncludeVat) vatEl.textContent = moneyNet(vatCents);
    if (totalEl) totalEl.textContent = formatGross(totalNet);
  };

  const bind = () => {
    document.addEventListener('ghalia:cart:stack-discount', (e) => {
      const drawer = document.querySelector('[data-cart-drawer]');
      applySummary(e.detail?.cart, e.detail?.state, drawer);
    });
    document.addEventListener('ghalia:cart-drawer:refreshed', () => {
      hideLiquidDiscountRows(document.querySelector('[data-cart-drawer]'));
    });
  };

  window.GhaliaCartSummary = { apply: applySummary };

  window.GhaliaPromo = {
    meetsMinimum: qualifyingMeetsPromoMinimum,
    shopifyDiscountNet: (cart) => {
      const fromLines = linePromoAllocationNet(cart);
      if (fromLines > 0) return fromLines;
      return Number(cart?.total_discount) || 0;
    },
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind);
  else bind();
})();
